import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { videoAdminAPI, filePublicUrl } from '../../services/api';
import { ExportMenu, DateRangeFilter, FilterSelect, ResetFiltersButton, filterSelectClass } from '../../components/SuperAdmin/ListTools';
import {
  ContentPageHeader, ContentStats, ContentListCard, ContentEmpty, ContentThumb, ContentRow, MetaDot,
  ReorderButtons, LiveToggle, RowActions, ContentModal, Field, FilePicker, inputClass,
} from '../../components/SuperAdmin/ContentAdmin';
import { inDateRange, matchesSearch, formatExportDate } from '../../utils/exportData';
import toast from 'react-hot-toast';
import { HiOutlineVideoCamera, HiOutlinePlay } from 'react-icons/hi';

const formatShortDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const Spinner = () => <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />;

const EMPTY_VIDEO_FILTERS = { search: '', status: '', sourceType: '', from: '', to: '' };

const VIDEO_EXPORT_COLUMNS = [
  { header: 'Title', value: (v) => v.title },
  { header: 'Slug', value: (v) => v.slug },
  { header: 'Description', value: (v) => v.description },
  { header: 'Source', value: (v) => (v.sourceType === 'url' ? 'External link' : 'Uploaded file') },
  { header: 'Video Link', value: (v) => v.videoUrl },
  { header: 'Status', value: (v) => (v.isActive ? 'Live' : 'Hidden') },
  { header: 'Order', value: (v) => v.order },
  { header: 'Created', value: (v) => formatExportDate(v.createdAt) },
  { header: 'Updated', value: (v) => formatExportDate(v.updatedAt) },
];

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const emptyForm = { title: '', slug: '', description: '', sourceType: 'url', videoUrl: '' };

const VideoModal = ({ editing, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(
    editing
      ? { title: editing.title, slug: editing.slug, description: editing.description || '', sourceType: editing.sourceType, videoUrl: editing.videoUrl || '' }
      : { ...emptyForm }
  );
  const [slugTouched, setSlugTouched] = useState(!!editing);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['superadmin-videos'] });

  const saveMutation = useMutation({
    mutationFn: () => {
      const formData = new FormData();
      formData.append('title', form.title.trim());
      formData.append('slug', form.slug.trim() || slugify(form.title));
      formData.append('description', form.description.trim());
      formData.append('sourceType', form.sourceType);
      if (form.sourceType === 'url') formData.append('videoUrl', form.videoUrl.trim());
      if (thumbnailFile) formData.append('thumbnail', thumbnailFile);
      if (form.sourceType === 'upload' && videoFile) formData.append('videoFile', videoFile);
      return editing ? videoAdminAPI.update(editing._id, formData) : videoAdminAPI.create(formData);
    },
    onSuccess: () => {
      invalidate();
      toast.success(editing ? 'Video updated' : 'Video created');
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save video'),
  });

  const handleTitleChange = (title) => {
    setForm((f) => ({ ...f, title, slug: slugTouched ? f.slug : slugify(title) }));
  };

  const canSave = form.title.trim() && (form.sourceType === 'url' ? form.videoUrl.trim() : (videoFile || editing?.videoFile?.url));

  const sourceTab = (value, label) => (
    <button
      type="button"
      onClick={() => setForm({ ...form, sourceType: value })}
      className={`px-4 py-1.5 rounded-md text-sm font-medium ${form.sourceType === value ? 'bg-white shadow text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
    >
      {label}
    </button>
  );

  return (
    <ContentModal
      title={editing ? 'Edit video' : 'New video'}
      subtitle="Shown on the website's Videos page as soon as you save"
      onClose={onClose}
      onSave={() => saveMutation.mutate()}
      saving={saveMutation.isPending}
      canSave={!!canSave}
    >
      <Field label="Title" required>
        <input type="text" value={form.title} onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="e.g. How to set up auto-dialing" className={inputClass} />
      </Field>
      <Field label="Slug" hint="Short ID for this video — filled in from the title">
        <input type="text" value={form.slug}
          onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: e.target.value }); }}
          placeholder="Filled in from the title" className={`${inputClass} font-mono`} />
      </Field>
      <Field label="Description" hint="Short text shown on the Videos list">
        <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className={inputClass} />
      </Field>

      <Field label="Video" required>
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1 w-fit mb-3">
          {sourceTab('url', 'YouTube / external link')}
          {sourceTab('upload', 'Upload a file')}
        </div>
        {form.sourceType === 'url' ? (
          <input type="text" value={form.videoUrl} onChange={(e) => setForm({ ...form, videoUrl: e.target.value })}
            placeholder="https://youtube.com/watch?v=…" className={inputClass} />
        ) : (
          <FilePicker image={false} accept="video/mp4,video/webm,video/quicktime" file={videoFile} onChange={setVideoFile}
            currentName={editing?.videoFile?.url ? `Current file: ${editing.videoFile.originalName}` : null}
            hint="MP4, WebM or MOV — up to 200MB" />
        )}
      </Field>

      <Field label="Thumbnail" hint="Shown before the video plays (16:9 image)">
        <FilePicker accept="image/*" file={thumbnailFile} onChange={setThumbnailFile}
          currentUrl={editing?.thumbnail?.url ? filePublicUrl(editing.thumbnail.url) : null} />
      </Field>
    </ContentModal>
  );
};

const Videos = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-videos'],
    queryFn: async () => (await videoAdminAPI.getAll()).data,
  });
  const videos = data?.data || [];
  const [filters, setFilters] = useState(EMPTY_VIDEO_FILTERS);
  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const filtersActive = Object.keys(EMPTY_VIDEO_FILTERS).some((k) => filters[k]);
  const visibleVideos = videos.filter((v) =>
    matchesSearch(filters.search, v.title, v.slug, v.description, v.videoUrl)
    && (!filters.status || (filters.status === 'live' ? v.isActive : !v.isActive))
    && (!filters.sourceType || v.sourceType === filters.sourceType)
    && inDateRange(v.createdAt, filters.from, filters.to));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['superadmin-videos'] });

  const toggleMutation = useMutation({ mutationFn: (id) => videoAdminAPI.toggle(id), onSuccess: invalidate });
  const deleteMutation = useMutation({
    mutationFn: (id) => videoAdminAPI.delete(id),
    onSuccess: () => { invalidate(); toast.success('Video deleted'); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete video'),
  });
  const reorderMutation = useMutation({
    mutationFn: (order) => videoAdminAPI.reorder(order),
    onSuccess: invalidate,
  });

  const move = (index, dir) => {
    const next = [...videos];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorderMutation.mutate(next.map((v) => v._id));
  };

  const openNew = () => { setEditing(null); setShowModal(true); };
  const openEdit = (v) => { setEditing(v); setShowModal(true); };

  return (
    <div className="p-6 space-y-6">
      <ContentPageHeader
        icon={HiOutlineVideoCamera}
        title="Videos"
        description="Videos on the website's Videos page — a YouTube link or an uploaded file. Changes go live as soon as you save."
        actionLabel="New Video"
        onAction={openNew}
      />

      {videos.length > 0 && <ContentStats items={videos} noun="videos" />}

      <ContentListCard
        filters={videos.length > 0 && (
          <>
            <input
              type="text"
              placeholder="Search title, slug, description, link…"
              value={filters.search}
              onChange={(e) => setFilter({ search: e.target.value })}
              className={`${filterSelectClass} flex-1 sm:min-w-[240px]`}
            />
            <FilterSelect value={filters.status} onChange={(v) => setFilter({ status: v })}
              allLabel="All Status" options={[['live', 'Live'], ['hidden', 'Hidden']]} />
            <FilterSelect value={filters.sourceType} onChange={(v) => setFilter({ sourceType: v })}
              allLabel="All Sources" options={[['url', 'External link'], ['upload', 'Uploaded file']]} />
            <DateRangeFilter label="Created" from={filters.from} to={filters.to}
              onChange={({ from, to }) => setFilter({ from, to })} />
            <ResetFiltersButton active={filtersActive} onReset={() => setFilters(EMPTY_VIDEO_FILTERS)} />
            <ExportMenu fileName="videos" columns={VIDEO_EXPORT_COLUMNS} getRows={() => visibleVideos} />
          </>
        )}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-14"><Spinner /></div>
        ) : videos.length === 0 ? (
          <ContentEmpty icon={HiOutlineVideoCamera} title="No videos yet"
            hint="Add a YouTube link or upload a file to show it on the Videos page." actionLabel="New Video" onAction={openNew} />
        ) : visibleVideos.length === 0 ? (
          <ContentEmpty icon={HiOutlineVideoCamera} title="No videos match these filters" />
        ) : (
          <div className="divide-y divide-gray-100">
            {visibleVideos.map((v, i) => (
              <ContentRow
                key={v._id}
                isActive={v.isActive}
                // Reordering a filtered subset would scramble the real order, so only when unfiltered
                reorder={!filtersActive && (
                  <ReorderButtons isFirst={i === 0} isLast={i === videos.length - 1}
                    disabled={reorderMutation.isPending} onUp={() => move(i, -1)} onDown={() => move(i, 1)} />
                )}
                thumb={<ContentThumb wide icon={HiOutlinePlay} src={v.thumbnail?.url ? filePublicUrl(v.thumbnail.url) : null} />}
                title={v.title}
                meta={(
                  <>
                    <span className={`px-1.5 py-0.5 rounded font-medium ${v.sourceType === 'url' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                      {v.sourceType === 'url' ? 'External link' : 'Uploaded file'}
                    </span>
                    <MetaDot /><span>{formatShortDate(v.createdAt)}</span>
                  </>
                )}
                description={v.description}
                controls={(
                  <>
                    <LiveToggle isActive={v.isActive} disabled={toggleMutation.isPending} onToggle={() => toggleMutation.mutate(v._id)} />
                    <RowActions name={v.title} onEdit={() => openEdit(v)} onDelete={() => deleteMutation.mutate(v._id)} />
                  </>
                )}
              />
            ))}
          </div>
        )}
      </ContentListCard>

      {showModal && <VideoModal editing={editing} onClose={() => setShowModal(false)} />}
    </div>
  );
};

export default Videos;
